export type OnboardingRole = 'OWNER' | 'ADMIN' | 'TRAINER' | 'PROMOTER';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  content: string[];
  targetSelector: string;
  path?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  highlight?: boolean;
}

const step = (
  partial: Omit<OnboardingStep, 'targetSelector' | 'highlight' | 'position'> & {
    targetSelector?: string;
    path?: string;
  }
): OnboardingStep => ({
  targetSelector: 'body',
  highlight: false,
  position: 'center',
  ...partial,
});

const welcome = (extra: string[]): OnboardingStep =>
  step({
    id: 'welcome',
    title: 'Добро пожаловать в ПРОФСПОРТСРМ',
    description: 'Как устроено это обучение',
    content: [
      'Это пошаговый обзор кабинета школы: что где находится и как обычно работают сотрудники.',
      'На каждом шаге откроется нужный раздел; читайте подсказки и жмите «Далее».',
      'Обучение можно пропустить и повторить позже в Настройках → «Повторить обучение».',
      'Рекомендуемый порядок запуска школы: филиал → группа → сотрудники → клиенты → абонементы → расписание.',
      ...extra,
    ],
  });

const complete: OnboardingStep = step({
  id: 'complete',
  title: 'Обучение завершено',
  description: 'Что делать дальше',
  content: [
    'Вы прошли обзор основных разделов кабинета.',
    'Если что-то забыли — раздел FAQ в меню: вкладки «Вопросы» и «Подробные материалы».',
    'Повторить обучение: Настройки → «Повторить обучение».',
    'По вопросам работы системы можно обратиться в поддержку (кнопка поддержки в интерфейсе).',
    'Удачной работы со школой!',
  ],
});

/** Полный подробный тур для OWNER (ADMIN — без тренировочного плана). */
const ownerAdminCore: OnboardingStep[] = [
  step({
    id: 'setup-order',
    title: 'С чего начать школу',
    description: 'Порядок первичной настройки',
    content: [
      '1) Создайте филиал (площадку) и при необходимости залы.',
      '2) Создайте группы и назначьте тренеров.',
      '3) Добавьте сотрудников (админы, тренеры, промоутеры).',
      '4) Заведите клиентов и выдайте абонементы.',
      '5) Составьте календарный план и отмечайте посещаемость.',
      'Ниже разберём каждый раздел подробнее.',
    ],
    path: '/dashboard',
  }),
  step({
    id: 'dashboard',
    title: 'Панель управления',
    description: 'Главная страница школы',
    content: [
      'Панель управления — стартовый экран после входа.',
      'Здесь сводка: клиенты, сотрудники, группы, филиалы, доходы и посещаемость.',
      'Удобно быстро оценить «состояние школы» за период.',
      'Из меню слева открываются все остальные разделы кабинета.',
      'Колокольчик уведомлений — зарплата, оплаты, задачи и сообщения.',
    ],
    path: '/dashboard',
    targetSelector: '[data-onboarding="dashboard"]',
  }),
  step({
    id: 'dashboard-stats',
    title: 'Карточки статистики',
    description: 'Ключевые метрики',
    content: [
      'Карточки показывают главные цифры школы.',
      'Данные обновляются по мере работы: новые клиенты, оплаты, занятия.',
    ],
    path: '/dashboard',
    targetSelector: '[data-onboarding="dashboard-stats"]',
  }),
  step({
    id: 'chats',
    title: 'Чаты',
    description: 'Переписка с клиентами и внутри школы',
    content: [
      'В чатах — диалоги с клиентами, родителями и сотрудниками.',
      'Чаты по задачам создаются из раздела «Заметки и задачи».',
      'Новые сообщения приходят в уведомления (колокольчик).',
      'Используйте чаты для оперативной связи, а не вместо карточки клиента.',
    ],
    path: '/chats',
    targetSelector: '[data-onboarding="chats-nav"]',
  }),
  step({
    id: 'staff-workspace-notes',
    title: 'Заметки',
    description: 'Личные и общие заметки сотрудников',
    content: [
      'Вкладка «Заметки»: личные (видите только вы) и общие.',
      'Общие заметки: «Видят все сотрудники школы» — удобно для инструкций и объявлений.',
      'Длинный текст корректно переносится и не «ломает» карточку.',
      'Редактировать/удалять можно свои заметки; общие — ещё и владелец школы.',
    ],
    path: '/staff-workspace',
    targetSelector: '[data-onboarding="staff-workspace-nav"]',
  }),
  step({
    id: 'staff-workspace-tasks',
    title: 'Задачи',
    description: 'Поручения сотрудникам',
    content: [
      'Создавать задачи могут сотрудники школы, кроме промоутера.',
      'Укажите заголовок, описание, дедлайн и хотя бы одного исполнителя.',
      'Можно сразу создать чат по задаче для обсуждения.',
      'Задачи также создаются из календаря (кнопка «Создать» → «Задача»).',
      'Просроченные задачи подсвечиваются; статус меняется на «Готово».',
    ],
    path: '/staff-workspace',
  }),
  step({
    id: 'clients-overview',
    title: 'Клиенты',
    description: 'База учеников школы',
    content: [
      'Раздел «Клиенты» — карточки всех учеников школы.',
      'В списке: ФИО, статус аккаунта, группа, абонемент, баланс.',
      'Поиск работает по ФИО, телефону и email.',
      'Сортировка по колонкам помогает быстро найти нужного клиента.',
      'Экспорт в Excel доступен для выгрузки базы (не у промоутера).',
    ],
    path: '/clients',
    targetSelector: '[data-onboarding="clients-nav"]',
  }),
  step({
    id: 'clients-add',
    title: 'Добавление клиента',
    description: 'Новая карточка',
    content: [
      'Кнопка «Добавить клиента» открывает форму создания.',
      'Заполните ФИО, контакты, пол, дату рождения и статус.',
      'После сохранения откроется карточка — туда добавляют группы и абонементы.',
      'Клиента можно сразу привязать к группе из карточки или списка.',
    ],
    path: '/clients',
    targetSelector: '[data-onboarding="add-client-button"]',
  }),
  step({
    id: 'clients-card',
    title: 'Карточка клиента',
    description: 'Что внутри карточки',
    content: [
      'Клик по имени в списке открывает карточку клиента.',
      'В карточке: контакты, родители, группы, абонементы, договоры, история.',
      'Выдача абонемента — из карточки (каталог тарифов школы).',
      'При выдаче с датой начала прошлые посещения клиента учитываются автоматически.',
      'Баланс и оплаты клиента связаны с разделом «Финансы».',
    ],
    path: '/clients',
    targetSelector: '[data-onboarding="clients-page"]',
  }),
  step({
    id: 'clients-packs',
    title: 'Абонементы клиента',
    description: 'Пакеты и пропуски',
    content: [
      'Пакетный абонемент списывает занятия при посещении.',
      'Если у клиента активный пакет, пропуск обычно не списывает занятие и не даёт ЗП «за человека» за этот пропуск.',
      'Групповая / ежемесячная оплата работает по правилам группы — смотрите настройки группы.',
      'Каталог тарифов настраивается в разделе «Абонементы».',
    ],
    path: '/clients',
  }),
  step({
    id: 'trainers-overview',
    title: 'Сотрудники',
    description: 'Тренеры, админы, промоутеры',
    content: [
      'Раздел «Сотрудники» — все сотрудники школы с доступом в кабинет.',
      'Роли: тренер, администратор, промоутер (узкий доступ только к своим клиентам).',
      'Клик по строке открывает карточку сотрудника.',
      'Отдельной кнопки «редактировать» в списке нет — всё через карточку.',
    ],
    path: '/trainers',
    targetSelector: '[data-onboarding="trainers-nav"]',
  }),
  step({
    id: 'trainers-add',
    title: 'Добавление сотрудника',
    description: 'Создание учётной записи',
    content: [
      '«Добавить сотрудника»: email, пароль, ФИО, роль, телефон.',
      'Для тренера задайте схему зарплаты (фикс, процент, за человека и т.д.).',
      'Промоутер создаёт клиентов с ограниченным набором полей и видит только своих.',
      'После создания сотрудник входит тем же единым входом.',
    ],
    path: '/trainers',
    targetSelector: '[data-onboarding="add-trainer-button"]',
  }),
  step({
    id: 'trainers-card',
    title: 'Карточка и зарплата',
    description: 'Баланс, филиалы, документы',
    content: [
      'Клик по балансу в списке открывает начисления и выплаты (диалог зарплаты).',
      'В карточке: контакты, схема ЗП, документы, назначение филиалов.',
      'Филиалы сотруднику назначаются прямо в карточке — отдельный диалог из списка убран.',
      'Уведомления о начислении ЗП ведут тренера в раздел заработка.',
    ],
    path: '/trainers',
    targetSelector: '[data-onboarding="trainers-page"]',
  }),
  step({
    id: 'groups-overview',
    title: 'Филиалы и группы',
    description: 'Структура школы в одном разделе',
    content: [
      'Раньше «Филиалы» и «Группы» были отдельно — теперь одно дерево.',
      'Сверху — филиал (площадка), внутри — его группы.',
      'Тренер видит дерево; создавать филиалы могут владелец школы и администраторы.',
    ],
    path: '/groups',
    targetSelector: '[data-onboarding="groups-nav"]',
  }),
  step({
    id: 'groups-branch',
    title: 'Филиал и залы',
    description: 'Площадка школы',
    content: [
      '«Добавить филиал»: название, адрес, контакты, старший тренер (если нужно).',
      'В редактировании филиала есть кнопка «Залы» — добавление/удаление залов площадки.',
      'Залы потом выбираются при создании тренировок.',
      'Без филиала нельзя нормально завести группы и расписание.',
    ],
    path: '/groups',
    targetSelector: '[data-onboarding="add-branch-button"]',
  }),
  step({
    id: 'groups-group',
    title: 'Группа',
    description: 'Учебная группа на филиале',
    content: [
      '«Добавить группу»: филиал, тренер, лимиты, возраст, цвет.',
      'Можно включить ежемесячную оплату: сумма и день выставления счёта.',
      'Счёт за месяц привязан к занятиям — пока незакрытый счёт есть, дубль не создаётся.',
      'Для группы настраивается схема зарплаты тренера (в т.ч. отдельная от личной).',
      'Состав группы редактируется из карточки группы / клиентов.',
    ],
    path: '/groups',
    targetSelector: '[data-onboarding="add-group-button"]',
  }),
  step({
    id: 'groups-tree',
    title: 'Работа с деревом',
    description: 'Навигация по филиалам',
    content: [
      'Группы сгруппированы под своим филиалом — так видно структуру площадок.',
      'Из строки группы: участники, расписание, быстрая смена филиала/тренера.',
      'Удаление филиала или группы требует осторожности, если есть связанные данные.',
    ],
    path: '/groups',
    targetSelector: '[data-onboarding="groups-page"]',
  }),
  step({
    id: 'schedule-overview',
    title: 'Календарный план',
    description: 'Тренировки, события, соревнования',
    content: [
      'Календарь объединяет регулярные и разовые тренировки, события и соревнования.',
      'Вкладки: «Расписание» и «Соревнования».',
      'Отсюда же отмечают посещаемость занятия.',
      'Цвета групп помогают ориентироваться в сетке.',
    ],
    path: '/schedule',
    targetSelector: '[data-onboarding="schedule-nav"]',
  }),
  step({
    id: 'schedule-create',
    title: 'Кнопка «Создать»',
    description: 'Тренировка / событие / задача',
    content: [
      'Одна кнопка «Создать» открывает меню из трёх пунктов.',
      '«Тренировка» — разовая или регулярная (по дням недели / датам).',
      '«Событие» — мероприятие школы; можно выбрать несколько групп, клиентов, родителей, тренеров.',
      '«Задача» — создаёт задачу сотрудникам с дедлайном на выбранный день календаря.',
      'При смене времени начала уже выбранная длительность занятия сохраняется (не сбрасывается на дефолт).',
    ],
    path: '/schedule',
    targetSelector: '[data-onboarding="schedule-create-button"]',
  }),
  step({
    id: 'schedule-attendance',
    title: 'Посещаемость',
    description: 'Отметка на занятии',
    content: [
      'Откройте тренировку в календаре → отметьте посещаемость клиента.',
      'Посещаемость влияет на списание абонемента и начисление ЗП тренеру.',
      'В диалоге посещаемости можно привязать тренировочный план (упражнения).',
      'Экспорт посещаемости в Excel доступен из связанных экранов.',
    ],
    path: '/schedule',
    targetSelector: '[data-onboarding="schedule-page"]',
  }),
  step({
    id: 'schedule-competitions',
    title: 'Соревнования и события',
    description: 'Вкладка соревнований',
    content: [
      'Вкладка «Соревнования» — учёт соревнований и связанных мероприятий.',
      'Тип «Другое мероприятие» тоже позволяет выбрать клиентов, родителей и тренеров.',
      'К событию можно привязать несколько групп сразу.',
      'Участников и тренеров видно в карточке события.',
    ],
    path: '/schedule',
    targetSelector: '[data-onboarding="schedule-competitions-tab"]',
  }),
  step({
    id: 'training-plan',
    title: 'Тренировочный план',
    description: 'Упражнения, шаблоны, циклы',
    content: [
      'Каталог упражнений (в т.ч. с медиа), шаблоны разминки/тренировки.',
      'Циклы связывают планы с существующими занятиями.',
      'План подключается в диалоге посещаемости конкретной тренировки.',
      'Раздел доступен владельцу школы и тренерам.',
    ],
    path: '/training-plan',
    targetSelector: '[data-onboarding="training-plan-nav"]',
  }),
  step({
    id: 'memberships-overview',
    title: 'Абонементы (каталог)',
    description: 'Тарифы школы',
    content: [
      'Здесь справочник тарифов: пакеты посещений и групповые/периодные варианты.',
      'Создайте тарифы до массовой выдачи клиентам.',
      'Неактивный тариф скрывается из выдачи, но история сохраняется.',
      'Выдача клиенту делается из карточки клиента, не из этого списка напрямую.',
    ],
    path: '/memberships',
    targetSelector: '[data-onboarding="memberships-nav"]',
  }),
  step({
    id: 'memberships-merge',
    title: 'Удаление и объединение тарифов',
    description: 'Порядок в каталоге',
    content: [
      'Удалить тариф можно, если нет активных выдач — иначе система предложит деактивировать.',
      '«Объединить»: выберите целевой тариф и источники.',
      'Все выдачи клиентам и привязки групп с источников переедут на целевой тариф.',
      'Источники после слияния деактивируются — в каталоге остаётся один актуальный тариф.',
    ],
    path: '/memberships',
    targetSelector: '[data-onboarding="memberships-page"]',
  }),
  step({
    id: 'finance-overview',
    title: 'Финансы',
    description: 'Оплаты и операции',
    content: [
      'Раздел «Финансы» — начисления, оплаты клиентов, операции школы.',
      'Следите за балансами клиентов и историей транзакций.',
      'Фильтры по периоду и типу операции помогают в сверке.',
      'QR и ссылки оплаты (если включены) упрощают приём платежей.',
    ],
    path: '/finance',
    targetSelector: '[data-onboarding="payments-nav"]',
  }),
  step({
    id: 'finance-page',
    title: 'Работа с финансами',
    description: 'Практические советы',
    content: [
      'Сначала выставляйте/проверяйте начисления, затем фиксируйте оплаты.',
      'Ошибочные операции лучше корректировать осознанно — от этого зависят отчёты и ЗП.',
      'Для контроля используйте фильтры и выгрузки, если они доступны на вкладках.',
    ],
    path: '/finance',
    targetSelector: '[data-onboarding="finance-page"]',
  }),
  step({
    id: 'settings',
    title: 'Настройки школы',
    description: 'Параметры кабинета',
    content: [
      'Длительность тренировки по умолчанию, права клиентов ЛК, уведомления.',
      'Видимость вкладок меню и другие параметры школы.',
      '«Повторить обучение» — снова запустить этот тур для текущего пользователя.',
      'Обучение хранится на пользователя: каждый сотрудник проходит его сам.',
    ],
    path: '/settings',
    targetSelector: '[data-onboarding="settings-page"]',
  }),
  step({
    id: 'faq',
    title: 'FAQ',
    description: 'Справка',
    content: [
      'Вкладка «Вопросы» — короткие ответы на типовые вопросы по работе в CRM.',
      'Вкладка «Подробные материалы» — развёрнутые инструкции для сотрудников школы.',
      'Если ответа нет — напишите в поддержку.',
    ],
    path: '/faq',
    targetSelector: '[data-onboarding="faq-page"]',
  }),
  step({
    id: 'knowledge-base',
    title: 'Подробные материалы',
    description: 'Справочник',
    content: [
      'Здесь собраны развёрнутые инструкции по разделам CRM.',
      'Используйте как справочник после прохождения обучения.',
    ],
    path: '/faq#materials',
    targetSelector: '[data-onboarding="knowledge-base-page"]',
  }),
];

const adminStepsFiltered = ownerAdminCore.filter((s) => s.id !== 'training-plan');

const trainerSteps: OnboardingStep[] = [
  step({
    id: 'welcome-trainer',
    title: 'Кабинет тренера',
    description: 'Что доступно вам',
    content: [
      'Вы работаете с клиентами, группами, расписанием и своим заработком.',
      'Можно создавать задачи коллегам и вести заметки.',
      'Финансы школы и каталог абонементов обычно ведут владелец школы и администраторы.',
      'Дальше — подробный обзор ваших разделов.',
    ],
    path: '/dashboard',
  }),
  step({
    id: 'dashboard',
    title: 'Панель управления',
    description: 'Сводка',
    content: [
      'На главной — обзор показателей, связанных с вашей работой в школе.',
      'Отсюда быстро переходите в расписание и к клиентам.',
      'Следите за уведомлениями: задачи, сообщения, зарплата.',
    ],
    path: '/dashboard',
  }),
  step({
    id: 'chats',
    title: 'Чаты',
    description: 'Связь',
    content: [
      'Пишите клиентам и коллегам в чатах школы.',
      'Чаты по задачам открываются из «Заметок и задач».',
      'Не дублируйте важную информацию только в чате — фиксируйте в карточке клиента при необходимости.',
    ],
    path: '/chats',
  }),
  step({
    id: 'staff-workspace',
    title: 'Заметки и задачи',
    description: 'Организация работы',
    content: [
      'Личные заметки — только для вас; общие — для всех сотрудников школы.',
      'Создавайте задачи себе и другим: дедлайн, исполнители, чат.',
      'Задачу также можно создать из календаря на конкретный день.',
    ],
    path: '/staff-workspace',
  }),
  step({
    id: 'clients',
    title: 'Клиенты',
    description: 'Работа с учениками',
    content: [
      'Открывайте карточку клиента: контакты, группы, абонементы.',
      'Посещаемость выставляете на тренировках в календаре — она влияет на пакеты.',
      'Если нет прав на удаление/финансы — обратитесь к администратору или владельцу.',
    ],
    path: '/clients',
  }),
  step({
    id: 'trainer-profile',
    title: 'Мой профиль',
    description: 'Ваши данные',
    content: [
      'Проверьте ФИО, телефон и данные профиля.',
      'Актуальные контакты нужны школе и клиентам для связи.',
    ],
    path: '/trainer/profile',
  }),
  step({
    id: 'trainer-earnings',
    title: 'Мой заработок',
    description: 'Начисления и выплаты',
    content: [
      'Здесь история начислений по вашей схеме зарплаты.',
      'Схема может быть общей или заданной на уровне группы.',
      'Уведомление о начислении открывает этот раздел.',
      'Вопросы по сумме — к владельцу/администратору школы.',
    ],
    path: '/trainer/earnings',
  }),
  step({
    id: 'groups',
    title: 'Филиалы и группы',
    description: 'Структура',
    content: [
      'Смотрите дерево филиалов и своих групп.',
      'Создание филиалов обычно только у владельца школы и администраторов — вам доступен просмотр и работа с группами по правам.',
      'Состав группы и расписание удобно сверять перед занятием.',
    ],
    path: '/groups',
  }),
  step({
    id: 'schedule',
    title: 'Календарный план',
    description: 'Занятия и посещаемость',
    content: [
      '«Создать»: тренировка, событие или задача на день.',
      'На занятии отмечайте посещаемость — это основа списаний и ЗП.',
      'Длительность при смене времени начала сохраняется.',
      'События можно связать с несколькими группами и участниками.',
    ],
    path: '/schedule',
  }),
  step({
    id: 'training-plan',
    title: 'Тренировочный план',
    description: 'Содержание занятий',
    content: [
      'Каталог упражнений и шаблоны помогают готовить тренировки.',
      'Циклы и планы подключаются к занятиям в посещаемости.',
      'Используйте раздел, чтобы стандартизировать работу с группами.',
    ],
    path: '/training-plan',
  }),
  step({
    id: 'settings',
    title: 'Настройки',
    description: 'Параметры и повтор обучения',
    content: [
      'Часть настроек школы доступна для просмотра; критичные меняет владелец школы и администраторы.',
      'Здесь же можно снова запустить обучение.',
    ],
    path: '/settings',
  }),
  step({
    id: 'help',
    title: 'FAQ',
    description: 'Справка',
    content: [
      '«Вопросы» — короткие ответы; «Подробные материалы» — развёрнутые инструкции.',
      'Если застряли — напишите в поддержку.',
    ],
    path: '/faq',
  }),
];

const promoterSteps: OnboardingStep[] = [
  step({
    id: 'promoter-role',
    title: 'Роль промоутера',
    description: 'Что можно и чего нельзя',
    content: [
      'Вам доступен в основном раздел «Клиенты».',
      'В списке только клиенты, которых создали вы лично.',
      'Нет доступа к финансам школы, расписанию и настройкам тарифов.',
      'Если нужна полная карточка или группа — передайте клиента администратору.',
    ],
    path: '/clients',
  }),
  step({
    id: 'clients-add',
    title: 'Добавление клиента',
    description: 'Упрощённая форма',
    content: [
      'Заполните: фамилия, имя, пол, дата рождения (возраст), телефон, статус.',
      'После сохранения клиент появится только в вашем списке.',
      'Проверьте телефон — по нему школа сможет найти человека позже.',
    ],
    path: '/clients',
    targetSelector: '[data-onboarding="add-client-button"]',
  }),
  step({
    id: 'clients-list',
    title: 'Ваш список клиентов',
    description: 'Ведение базы',
    content: [
      'Ищите клиентов через строку поиска.',
      'Открывайте карточку, чтобы уточнить данные.',
      'Не создавайте дубликаты — сначала поищите по ФИО/телефону.',
      'По вопросам зачисления в группу или абонемента — к администратору школы.',
    ],
    path: '/clients',
    targetSelector: '[data-onboarding="clients-page"]',
  }),
  step({
    id: 'promoter-tips',
    title: 'Практические советы',
    description: 'Как работать эффективно',
    content: [
      'Фиксируйте актуальный телефон и статус сразу при создании.',
      'Если клиент уже есть в школе — не создавайте второго, сообщите админу.',
      'Ваша задача — привести и завести человека; дальнейшее ведение часто за школой.',
    ],
    path: '/clients',
  }),
];

const promoterComplete: OnboardingStep = step({
  id: 'complete',
  title: 'Готово',
  description: 'Обучение промоутера завершено',
  content: [
    'Вы умеете создавать и вести своих клиентов.',
    'При необходимости попросите администратора расширить данные карточки.',
    'Удачной работы!',
  ],
});

const trainerComplete: OnboardingStep = step({
  id: 'complete',
  title: 'Готово',
  description: 'Обучение тренера завершено',
  content: [
    'Вы знаете расписание, посещаемость, клиентов, задачи и заработок.',
    'Повторить тур: Настройки → «Повторить обучение».',
    'Удачной работы с группами!',
  ],
});

export function getOnboardingSteps(role: string): OnboardingStep[] {
  switch (role) {
    case 'OWNER':
      return [
        welcome([
          'Вы — владелец: полный доступ ко всем разделам и настройкам.',
        ]),
        ...ownerAdminCore,
        complete,
      ];
    case 'ADMIN':
      return [
        welcome([
          'Вы — администратор: почти полный доступ, как у владельца (кроме отдельных ограничений).',
        ]),
        ...adminStepsFiltered,
        complete,
      ];
    case 'TRAINER':
      return [
        welcome(['Вы — тренер: фокус на группах, расписании и заработке.']),
        ...trainerSteps,
        trainerComplete,
      ];
    case 'PROMOTER':
      return [
        welcome(['Вы — промоутер: доступ ограничен вашими клиентами.']),
        ...promoterSteps,
        promoterComplete,
      ];
    default:
      return [welcome([]), complete];
  }
}
